/**
 * Secure localStorage utilities with encryption
 * 
 * This module provides secure storage functions that encrypt/decrypt data
 * before storing in localStorage.
 */

import { Encrypt, Decrypt, isEncryptionConfigured } from './encryption';
import { getEncryptKey } from './obfuscate';
import CryptoJS from 'crypto-js';

/**
 * Mapping of original keys to encrypted keys
 * This ensures consistent encrypted key names across the application
 */
const KEY_MAP: Record<string, string> = {
  'accountId': 'U2FsdGVkX1+ZtHSlZ3SDASVHF',
  'cached-config': 'U2FsdGVkX19mK2pQR1hMNXVj',
  'cookie-consent': 'U2FsdGVkX18xN3JzVGhyZWU0',
  'favorites': 'U2FsdGVkX1+FourFiveSix789',
  'recentlyViewedVehicles': 'U2FsdGVkX19SevenEightNine0',
  'selected-store-id': 'U2FsdGVkX1+TenElevenTwelv',
  'vehicle-short-codes': 'U2FsdGVkX19ThirteenFourte',
  'selected-layout': 'U2FsdGVkX19LayoutSelection',
  'selected-layout-temp': 'U2FsdGVkX19LayoutTempSel'
};

/**
 * Get encrypted key for localStorage
 * @param originalKey - The original key name
 * @returns The encrypted key to use in localStorage
 */
const getEncryptedKey = (originalKey: string): string => {
  // If key is already in the map, return it
  if (KEY_MAP[originalKey]) {
    return KEY_MAP[originalKey];
  }
  
  // For dynamic keys, generate a consistent encrypted key
  const encryptionKey = getEncryptKey() || 'default-key';
  const hash = CryptoJS.SHA256(originalKey + encryptionKey).toString();
  return 'U2FsdGVkX1' + hash.substring(0, 16);
};

/**
 * Securely store data in localStorage with encryption
 * 
 * @param key - The localStorage key
 * @param value - The value to store (will be encrypted)
 * @returns Promise<boolean> - true if successful, false otherwise
 */
export const secureSetItem = async (key: string, value: any): Promise<boolean> => {
  try {
    // Get encrypted key
    const encryptedKey = getEncryptedKey(key);
    
    // Check if encryption is configured
    if (!isEncryptionConfigured()) {
      console.warn('Encryption not configured, storing data unencrypted');
      localStorage.setItem(encryptedKey, JSON.stringify(value));
      return true;
    }

    // Encrypt the data
    const encryptedData = await Encrypt(value);
    
    // Store encrypted data with encrypted key
    localStorage.setItem(encryptedKey, encryptedData);
    
    return true;
  } catch (error) {
    console.error('Error storing encrypted data:', error);
    // Fallback to unencrypted storage
    try {
      const encryptedKey = getEncryptedKey(key);
      localStorage.setItem(encryptedKey, JSON.stringify(value));
      return true;
    } catch (fallbackError) {
      console.error('Error storing data:', fallbackError);
      return false;
    }
  }
};

/**
 * Securely retrieve data from localStorage with decryption
 * 
 * @param key - The localStorage key
 * @returns Promise<any | null> - The decrypted value or null if not found
 */
export const secureGetItem = async (key: string): Promise<any | null> => {
  try {
    // Get encrypted key
    const encryptedKey = getEncryptedKey(key);
    const storedData = localStorage.getItem(encryptedKey);
    
    if (!storedData) {
      // Try fallback with original key for backward compatibility
      const fallbackData = localStorage.getItem(key);
      if (fallbackData) {
        // Migrate old data to encrypted key
        try {
          const parsedData = JSON.parse(fallbackData);
          await secureSetItem(key, parsedData);
          localStorage.removeItem(key);
          return parsedData;
        } catch (parseError) {
          return fallbackData;
        }
      }
      return null;
    }

    // Check if encryption is configured
    if (!isEncryptionConfigured()) {
      console.warn('Encryption not configured, reading data as JSON');
      try {
        return JSON.parse(storedData);
      } catch {
        return storedData;
      }
    }

    // Try to decrypt the data
    try {
      const decryptedData = await Decrypt(storedData);
      return decryptedData;
    } catch (decryptError) {
      // If decryption fails, try to parse as JSON (backward compatibility)
      console.warn('Failed to decrypt, trying to parse as JSON');
      try {
        return JSON.parse(storedData);
      } catch {
        return storedData;
      }
    }
  } catch (error) {
    console.error('Error retrieving data:', error);
    return null;
  }
};

/**
 * Remove item from localStorage
 * 
 * @param key - The localStorage key to remove
 */
export const secureRemoveItem = (key: string): void => {
  try {
    const encryptedKey = getEncryptedKey(key);
    localStorage.removeItem(encryptedKey);
    // Also remove old key for backward compatibility
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing item:', error);
  }
};

/**
 * Check if a key exists in localStorage
 * 
 * @param key - The localStorage key to check
 * @returns boolean - true if key exists
 */
export const secureHasItem = (key: string): boolean => {
  try {
    const encryptedKey = getEncryptedKey(key);
    return localStorage.getItem(encryptedKey) !== null || localStorage.getItem(key) !== null;
  } catch (error) {
    console.error('Error checking item:', error);
    return false;
  }
};

/**
 * Clear all items from localStorage
 * Warning: This will remove all stored data
 */
export const secureClear = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

